import React from "react";
import { RightCircleOutlined } from "@ant-design/icons";
import { Button, Popover, Spin } from "antd";
import { Link, useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useShopInfoStore } from "../../store";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import QuickEnum from "@/scenes/OrderManagement/enum/QuickEnum";
import { collectEvent } from "@/services/dataTrack";
import classNames from "classnames";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import AuthTokenHolder from "@/services/http/AuthTokenHolder";
import { FD_BASE_URL } from "@/config";
import { useGlobalStore } from "@/store/hooks";
import CountValue from "../../CountValue";

interface OrderAreaProps {}

const OrderArea: React.FC<OrderAreaProps> = function (props) {
    const store = useShopInfoStore();
    const history = useHistory();
    const globalStore = useGlobalStore();

    function handlePrint() {
        // 跳转到第一个有待发货订单的店铺中
        const shopOverviewResult = store.shopOverviewResults.find(
            ({ totalPendingShipmentOrders }) => totalPendingShipmentOrders,
        );

        if (shopOverviewResult) {
            const shopId = shopOverviewResult.shopId;

            history.push(`/order/list?shopId=${shopId}&tab=${OrderTab.PENDING_SHIPMENT}`);
        } else {
            history.push(`/order/list`);
        }

        collectEvent(DataTrackEvent.CLICK_HOME_CONTENT, {
            content: "打印待发货订单",
        });
    }

    function handleLogistics() {
        const shopOverviewResult = store.shopOverviewResults.find(
            ({ totalLogisticsWarningOrders }) => totalLogisticsWarningOrders,
        );

        if (shopOverviewResult) {
            const shopId = shopOverviewResult.shopId;

            history.push(`/order/list?shopId=${shopId}&tab=${OrderTab.LOGISTICS_WARNING}`);
        } else {
            history.push(`/order/list`);
        }

        collectEvent(DataTrackEvent.CLICK_HOME_CONTENT, {
            content: "查看物流预警订单",
        });
    }

    function handleRefund() {
        const shopOverviewResult = store.shopOverviewResults.find(({ totalRefundingOrders }) => totalRefundingOrders);

        if (shopOverviewResult) {
            const shopId = shopOverviewResult.shopId;

            history.push(`/order/list?shopId=${shopId}&tab=${OrderTab.PENDING_SHIPMENT_REFUNDING}`);
        } else {
            history.push(`/order/list`);
        }

        collectEvent(DataTrackEvent.CLICK_HOME_CONTENT, {
            content: "处理退款中订单",
        });
    }

    function handleShippingDeadline() {
        const shopOverviewResult = store.shopOverviewResults.find(
            ({ shippingDeadlineOrderCount }) => shippingDeadlineOrderCount,
        );

        if (shopOverviewResult) {
            const shopId = shopOverviewResult.shopId;

            history.push(
                `/order/list?shopId=${shopId}&tab=${OrderTab.PENDING_SHIPMENT}&quick=${QuickEnum.QUICK_DELIVERY_TIMEOUT}`,
            );
        } else {
            history.push(`/order/list`);
        }

        collectEvent(DataTrackEvent.CLICK_HOME_CONTENT, {
            content: "处理即将超时发货订单",
        });
    }

    function handleAbnormal() {
        const shopOverviewResult = store.shopOverviewResults.find(({ hasAbnormalOrders }) => hasAbnormalOrders);

        if (shopOverviewResult) {
            const shopId = shopOverviewResult.shopId;

            history.push(`/order/list?shopId=${shopId}&tab=${OrderTab.ABNORMAL}`);
        } else {
            history.push(`/order/list`);
        }

        collectEvent(DataTrackEvent.CLICK_HOME_CONTENT, {
            content: "处理异常订单",
        });
    }

    function getIframeOutsideUrl() {
        const search = new URLSearchParams();
        const token = AuthTokenHolder?.getToken?.();

        search.set("_token_", token);

        return `${FD_BASE_URL}/order/list?${search.toString()}`;
    }

    function buildIframeBtn(tab: OrderTab) {
        function getBtnText(tab: OrderTab) {
            switch (tab) {
                case OrderTab.PENDING_SHIPMENT:
                    return "去打印";
                case OrderTab.ABNORMAL:
                    return "去处理";
                case OrderTab.LOGISTICS_WARNING:
                    return "去查看";
                case OrderTab.PENDING_SHIPMENT_REFUNDING:
                    return "去处理";
                default:
                    return "查看";
            }
        }

        const search = new URLSearchParams();
        const token = AuthTokenHolder?.getToken?.();

        search.set("_token_", token);

        let shopOverviewResult;

        switch (tab) {
            case OrderTab.PENDING_SHIPMENT:
                shopOverviewResult = store.shopOverviewResults?.find(
                    ({ totalPendingShipmentOrders }) => totalPendingShipmentOrders,
                );
                break;

            case OrderTab.ABNORMAL:
                shopOverviewResult = store.shopOverviewResults?.find(({ hasAbnormalOrders }) => hasAbnormalOrders);
                break;

            case OrderTab.LOGISTICS_WARNING:
                shopOverviewResult = store.shopOverviewResults?.find(
                    ({ totalLogisticsWarningOrders }) => totalLogisticsWarningOrders,
                );
                break;

            case OrderTab.PENDING_SHIPMENT_REFUNDING:
                shopOverviewResult = store.shopOverviewResults?.find(
                    ({ totalRefundingOrders }) => totalRefundingOrders,
                );
                break;

            default:
                break;
        }

        if (shopOverviewResult) {
            search.set("shopId", shopOverviewResult.shopId.toString());
        }

        search.set("tab", tab);

        return (
            <a target="_blank" href={`${FD_BASE_URL}/order/list?${search.toString()}`}>
                {getBtnText(tab)}
            </a>
        );
    }

    function buildShippingDeadlineIframeBtn() {
        const search = new URLSearchParams();
        const token = AuthTokenHolder?.getToken?.();

        search.set("_token_", token);

        const shopOverviewResult = store.shopOverviewResults.find(
            ({ shippingDeadlineOrderCount }) => shippingDeadlineOrderCount,
        );

        if (shopOverviewResult) {
            search.set("shopId", shopOverviewResult.shopId.toString());
        }

        search.set("tab", OrderTab.PENDING_SHIPMENT);
        search.set("quick", QuickEnum.QUICK_DELIVERY_TIMEOUT);

        return (
            <a target="_blank" href={`${FD_BASE_URL}/order/list?${search.toString()}`}>
                去处理
            </a>
        );
    }

    return (
        <div className="order-area">
            <Spin classNames={{ root: "order-area-wrapper" }} spinning={false}>
                {/* 待发货订单 */}
                <div className="order-area-item">
                    <Popover
                        content="按店铺订单最后同步时间，汇总近三个月内待发货订单（不包括退款中、挂起和预发货订单）"
                        placement="topLeft"
                    >
                        <div className="title">待发货订单</div>
                    </Popover>

                    <div className="count">
                        <CountValue
                            value={
                                store.getShopOverviewLoading
                                    ? undefined
                                    : store.getCount(store.totalPendingShipmentOrders)
                            }
                        />
                    </div>
                    <div className="operation">
                        {globalStore.isIframe ? (
                            buildIframeBtn(OrderTab.PENDING_SHIPMENT)
                        ) : (
                            <Button type="link" onClick={handlePrint}>
                                去打印
                            </Button>
                        )}
                    </div>
                </div>

                {/* 异常订单 */}
                <div className="order-area-item">
                    <Popover content="近7天的异常订单" placement="topLeft">
                        <div className="title">异常订单</div>
                    </Popover>

                    <div className={classNames("count", store.isAnyShopHasAbnormalOrder && "error")}>
                        <CountValue
                            value={
                                store.getShopOverviewLoading
                                    ? undefined
                                    : store.isAnyShopHasAbnormalOrder === undefined
                                    ? "-"
                                    : store.isAnyShopHasAbnormalOrder
                                    ? "有"
                                    : "无"
                            }
                        />
                    </div>
                    <div className="operation">
                        {globalStore.isIframe ? (
                            buildIframeBtn(OrderTab.ABNORMAL)
                        ) : (
                            <Button type="link" onClick={handleAbnormal}>
                                去处理
                            </Button>
                        )}
                    </div>
                </div>

                {/* 物流预警订单 */}
                <div className="order-area-item">
                    <Popover content="已开启物流异常预警的店铺，近期物流预警的订单">
                        <div className="title">物流预警订单</div>
                    </Popover>

                    <div className="count">
                        <CountValue
                            value={
                                store.getShopOverviewLoading
                                    ? undefined
                                    : store.getCount(store.totalLogisticsWarningOrders)
                            }
                        />
                    </div>
                    <div className="operation">
                        {globalStore.isIframe ? (
                            buildIframeBtn(OrderTab.LOGISTICS_WARNING)
                        ) : (
                            <Button type="link" onClick={handleLogistics}>
                                去查看
                            </Button>
                        )}
                    </div>
                </div>

                {/* 即将发货超时 */}
                <div className="order-area-item">
                    <Popover content="即将发货超时订单">
                        <div className="title">即将发货超时订单</div>
                    </Popover>

                    <div className="count">
                        <CountValue
                            value={
                                store.getShopOverviewLoading
                                    ? undefined
                                    : store.getCount(store.shippingDeadlineOrderCount)
                            }
                        />
                    </div>
                    <div className="operation">
                        {globalStore.isIframe ? (
                            buildShippingDeadlineIframeBtn()
                        ) : (
                            <Button type="link" onClick={handleShippingDeadline}>
                                去处理
                            </Button>
                        )}
                    </div>
                </div>

                {/* 退款中订单 */}
                <div className="order-area-item">
                    <Popover content="待发货退款中订单">
                        <div className="title">退款中订单</div>
                    </Popover>

                    <div className="count">
                        <CountValue
                            value={
                                store.getShopOverviewLoading ? undefined : store.getCount(store.totalRefundingOrders)
                            }
                        />
                    </div>
                    <div className="operation">
                        {globalStore.isIframe ? (
                            buildIframeBtn(OrderTab.PENDING_SHIPMENT_REFUNDING)
                        ) : (
                            <Button type="link" onClick={handleRefund}>
                                去处理
                            </Button>
                        )}
                    </div>
                </div>
            </Spin>

            {/* 底部按钮 */}
            <div className="ml10 mr10">
                <p className="mt10">
                    {globalStore.isIframe ? (
                        <a target="_blank" href={getIframeOutsideUrl()}>
                            <Button
                                type="primary"
                                size="large"
                                onClick={() => {
                                    collectEvent(DataTrackEvent.CLICK_HOME_CONTENT, {
                                        content: "开始打印发货",
                                    });
                                }}
                            >
                                开始打印发货 <RightCircleOutlined type="right" />
                            </Button>
                        </a>
                    ) : (
                        <Link to="/order/list">
                            <Button
                                type="primary"
                                size="large"
                                onClick={() => {
                                    collectEvent(DataTrackEvent.CLICK_HOME_CONTENT, {
                                        content: "开始打印发货",
                                    });
                                }}
                            >
                                开始打印发货 <RightCircleOutlined type="right" />
                            </Button>
                        </Link>
                    )}
                </p>
            </div>
        </div>
    );
};

export default observer(OrderArea);
